import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSpinner } from '@/fontawesome';
import Modal from '@/components/ui/Modal';
import type { User } from '@/models';
import { APIRoutes } from '@/services/api.routes';
import { showToast } from '@/utils/toasts';
import { normalizePhoneNumber, validatePhoneE164 } from '@/utils/helpers';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onUserUpdated: (updatedUser: User) => void;
}

export default function EditProfileModal({ isOpen, onClose, user, onUserUpdated }: EditProfileModalProps) {
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [phone, setPhone] = useState(user.phone);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim()) {
      showToast('Vor- und Nachname müssen ausgefüllt werden', 'error');
      return;
    }

    if (phone.trim() !== '') {
      const phoneValidation = validatePhoneE164(phone);
      if (!phoneValidation.valid) {
        showToast(phoneValidation.error ?? 'Ungültige Telefonnummer', 'error');
        return;
      }
    }

    setIsLoading(true);
    try {
      const updatedUser = await APIRoutes.updateCurrentUser({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: normalizePhoneNumber(phone)
      });

      onUserUpdated(updatedUser);
      showToast('Profil erfolgreich aktualisiert', 'success');
      onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast('Fehler beim Aktualisieren des Profils', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      // Reset form to original values
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setPhone(user.phone);
      setPhoneTouched(false);
      onClose();
    }
  };

  const hasChanges =
    firstName.trim() !== user.firstName ||
    lastName.trim() !== user.lastName ||
    phone.trim() !== user.phone;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Profil bearbeiten"
      icon={<FontAwesomeIcon icon={faEdit} className="w-5 h-5 text-blue-600" />}
      maxWidth="md"
      closeDisabled={isLoading}
    >
      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Vorname
            </label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
              required
            />
          </div>

          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nachname
            </label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
              required
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Telefon <span className="text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onBlur={() => setPhoneTouched(true)}
              disabled={isLoading}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 dark:bg-gray-700 dark:text-white disabled:opacity-50 ${
                phoneTouched && phone.trim() !== '' && !validatePhoneE164(phone).valid
                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="+491701234567"
            />
            {phoneTouched && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Mit Ländervorwahl, z. B. +491701234567
              </p>
            )}
            {phoneTouched && phone.trim() !== '' && !validatePhoneE164(phone).valid && (
              <p className="mt-1 text-xs text-red-500">
                Telefonnummer muss mit Ländervorwahl beginnen (z. B. +491701234567)
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={isLoading || !hasChanges}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center"
          >
            {isLoading && <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />}
            Speichern
          </button>
        </div>
      </form>
    </Modal>
  );
}
